import { useState } from "react";
import Navbar from "@/components/Navbar";
import QuotaBanner from "@/components/QuotaBanner";
import HistoryGraph from "@/components/HistoryGraph";
import DirectorChat from "@/components/DirectorChat";
import { useApp } from "@/context/AppContext";
import { useT } from "@/lib/i18n";
import { STUDIO } from "@/constants/testIds";
import ConceptQuiz from "@/pages/modules/ConceptQuiz";
import EpisodeGenerator from "@/pages/modules/EpisodeGenerator";
import CharacterKit from "@/pages/modules/CharacterKit";
import Calendar30 from "@/pages/modules/Calendar30";
import ViralLab from "@/pages/modules/ViralLab";
import Monetization from "@/pages/modules/Monetization";
import EditingTemplates from "@/pages/modules/EditingTemplates";
import VoiceAudio from "@/pages/modules/VoiceAudio";
import Trends from "@/pages/modules/Trends";
import Library from "@/pages/modules/Library";
import Referral from "@/pages/modules/Referral";
import SourceImport from "@/pages/modules/SourceImport";
import Projects from "@/pages/modules/Projects";
import { Sparkles, Wand2, Cat, Calendar, TrendingUp, Wallet, Scissors, Mic2, Flame, Library as LibraryIcon, Gift, Youtube, FolderKanban } from "lucide-react";

const TABS = [
    { id: "projects", labels: {fr:"Projets",         en:"Projects",       es:"Proyectos"},        icon: FolderKanban,testid: "studio-tab-projects",Comp: Projects },
    { id: "quiz",      labels: {fr:"Niche Finder",   en:"Niche Finder",   es:"Buscador Nicho"},   icon: Sparkles,    testid: STUDIO.quizTab,      Comp: ConceptQuiz },
    { id: "source",    labels: {fr:"Importe source", en:"Import source",  es:"Importar fuente"},  icon: Youtube,     testid: "studio-tab-source", Comp: SourceImport },
    { id: "episode",   labels: {fr:"Épisodes",       en:"Episodes",       es:"Episodios"},        icon: Wand2,       testid: STUDIO.episodeTab,   Comp: EpisodeGenerator },
    { id: "character", labels: {fr:"Personnages",    en:"Characters",     es:"Personajes"},       icon: Cat,         testid: STUDIO.characterTab, Comp: CharacterKit },
    { id: "calendar",  labels: {fr:"Calendrier",     en:"Calendar",       es:"Calendario"},       icon: Calendar,    testid: STUDIO.calendarTab,  Comp: Calendar30 },
    { id: "trends",    labels: {fr:"Tendances",      en:"Trends",         es:"Tendencias"},       icon: Flame,       testid: "studio-tab-trends", Comp: Trends },
    { id: "viral",     labels: {fr:"Viral Lab",      en:"Viral Lab",      es:"Viral Lab"},        icon: TrendingUp,  testid: STUDIO.viralTab,     Comp: ViralLab },
    { id: "money",     labels: {fr:"Monétisation",   en:"Monetization",   es:"Monetización"},     icon: Wallet,      testid: STUDIO.moneyTab,     Comp: Monetization },
    { id: "edit",      labels: {fr:"Montage",        en:"Editing",        es:"Edición"},          icon: Scissors,    testid: STUDIO.editTab,      Comp: EditingTemplates },
    { id: "voice",     labels: {fr:"Voix & Audio",   en:"Voice & Audio",  es:"Voz y Audio"},      icon: Mic2,        testid: STUDIO.voiceTab,     Comp: VoiceAudio },
    { id: "library",   labels: {fr:"Bibliothèque",   en:"Library",        es:"Biblioteca"},       icon: LibraryIcon, testid: "studio-tab-library",Comp: Library },
    { id: "referral",  labels: {fr:"Parrainage",     en:"Referral",       es:"Referidos"},        icon: Gift,        testid: "studio-tab-referral",Comp: Referral },
];

export default function Studio() {
    const { lang, user } = useApp();
    const t = useT(lang);
    const [active, setActive] = useState("projects");
    const ActiveComp = TABS.find((x) => x.id === active)?.Comp;

    return (
        <div className="min-h-screen bg-[#080B10]">
            <Navbar />
            <div data-testid={STUDIO.container} className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                    <div>
                        <div className="overline">Studio · session {user?.name || user?.email}</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase mt-1">Shorts <span className="flame-text">Factory</span></h1>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono-sf text-slate-500">
                        <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full rec-dot" /> ENGINE ONLINE · Claude Sonnet 4.5
                    </div>
                </div>

                <div data-testid={STUDIO.tabsList} className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-12 gap-2 mb-6">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const on = active === tab.id;
                        return (
                            <button
                                key={tab.id}
                                data-testid={tab.testid}
                                onClick={() => setActive(tab.id)}
                                className={`flex flex-col items-start gap-1.5 p-3 rounded-md border kinetic text-left ${on ? "border-[#FF4D00] bg-gradient-to-b from-[#1E2738] to-[#161D2A]" : "border-[#253046] bg-[#161D2A] hover:border-[#3B4B6C]"}`}
                            >
                                <Icon className={`w-4 h-4 ${on ? "text-[#FF4D00]" : "text-slate-400"}`} strokeWidth={1.8} />
                                <span className={`font-display text-sm font-bold uppercase ${on ? "text-white" : "text-slate-300"}`}>
                                    {tab.labels[lang] || tab.labels.en}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <QuotaBanner onOpenLibrary={() => setActive("library")} />
                {active !== "projects" && <HistoryGraph />}

                <div className="rounded-lg border border-[#253046] bg-[#0F141C] p-4 sm:p-6 min-h-[560px]">
                    {ActiveComp && <ActiveComp />}
                </div>
            </div>
            <DirectorChat onNavigate={(id) => setActive(id)} />
        </div>
    );
}
